/* InkBoard ― 同期層
 *
 * 考え方
 *   ・手元の保存（IndexedDB）はこれまでどおり。電波がなくても描けて、消えない
 *   ・Yjs は「端末どうしをつなぐ道」として使う。サーバ側にも履歴が残る
 *   ・1ストローク＝1つの値。点ごとには分解しない（容量と処理が破綻するため）
 *   ・カードは1枚＝1つの値。同じカードを2台で同時に書き換えたら後勝ち
 *
 * ドキュメントの分け方
 *   "index"      … ボードの一覧（id → 名前・更新時刻）
 *   "board:<id>" … そのボードの中身（cards / strokes / meta）
 */
import * as Y from "yjs";
import { HocuspocusProvider } from "@hocuspocus/provider";
import * as app from "./app.js";

const SETTINGS_KEY = "inkboard.sync";
const PUSH_DELAY = 500;      // 手元の変更をまとめて送るまでの待ち
const PULL_DELAY = 250;      // 遠隔の変更を画面へ反映するまでの待ち

/* ── 接続先の設定 ───────────────────────────── */
function loadSettings() {
  try { return JSON.parse(localStorage.getItem(SETTINGS_KEY)) || {}; }
  catch (e) { return {}; }
}
function saveSettings(s) {
  try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(s)); } catch (e) {}
}
/** 既定の接続先。同じサーバから配られていれば自動で決まる */
function defaultUrl() {
  if (location.protocol === "file:") return "";
  const scheme = location.protocol === "https:" ? "wss" : "ws";
  return `${scheme}://${location.host}/sync`;
}

const settings = loadSettings();
if (!settings.url) settings.url = defaultUrl();

/* ── 接続の状態を1か所で持つ ──────────────────── */
const state = {
  url: settings.url || "",
  token: settings.token || "",
  space: settings.space || "",
  enabled: settings.enabled !== false,
  status: "off",                 // off | connecting | online | error
  indexDoc: null, indexProvider: null,
  boardDoc: null, boardProvider: null, boardId: null,
  applyingRemote: false,
  pushTimer: null, pullTimer: null, autoOpened: false,
  knownRemote: new Set(),        // 遠隔の一覧に載っていたボード。消えたことを見分けるため
  lastSent: new Map()            // 送信済みの内容。差分だけ送るための控え
};

/* ── 画面右上の表示 ─────────────────────────── */
const badge = document.createElement("button");
badge.className = "btn";
badge.id = "syncBadge";
badge.title = "同期の設定";
document.addEventListener("DOMContentLoaded", mountBadge);
mountBadge();

function mountBadge() {
  const boards = document.getElementById("btnBoards");
  if (!boards || badge.isConnected) return;
  boards.parentNode.insertBefore(badge, boards);
  badge.addEventListener("click", openSettings);
  paint();
}
function paint() {
  const label = { off: "同期オフ", connecting: "接続中…", online: "同期中", error: "つながりません" };
  badge.textContent = label[state.status] || "同期";
  // つながらないときに、どこへ繋ぎに行っているかが分かるように
  badge.title = "同期の設定\nスペース: " + (state.space || "（既定）") +
                "\n接続先: " + (state.url || "(未設定)") +
                "\n状態: " + (label[state.status] || state.status);
  // どの部屋にいるかが一目で分かるように
  badge.textContent = state.space
    ? (label[state.status] || "同期") + "・" + state.space
    : (label[state.status] || "同期");
  badge.style.borderColor = state.status === "online" ? "var(--accent)" : "";
  badge.style.color = state.status === "error" ? "#c2453d" : "";
}

function openSettings() {
  const url = prompt(
    "同期サーバのアドレス\n" +
    "空欄にすると、いま開いているサーバを自動で使います（おすすめ）\n" +
    "自動のときの接続先: " + (defaultUrl() || "なし"),
    state.url
  );
  if (url === null) return;
  const token = prompt("合言葉（サーバの SYNC_TOKEN と同じもの）", state.token);
  if (token === null) return;

  const space = prompt(
    "スペース名（部屋の名前）\n" +
    "空欄 … これまでの置き場をそのまま使う\n" +
    "同じ名前を入れた人どうしで、ボードを共有します\n" +
    "例）家族 / 自分用 / 仕事",
    state.space
  );
  if (space === null) return;

  const spaceChanged = space.trim() !== (state.space || "");
  state.url = url.trim() || defaultUrl();
  state.token = token.trim();
  state.space = space.trim();
  state.enabled = !!state.url;
  saveSettings({ url: state.url, token: state.token, space: state.space, enabled: state.enabled });

  if (spaceChanged) {
    // スペースが変わると手元の保存先ごと変わるので、読み込み直すのが確実
    app.toast("スペースを切り替えます…");
    setTimeout(() => location.reload(), 600);
    return;
  }
  connect();
}

/* ── スペース ───────────────────────────────
 * 同じサーバの中を、いくつかの部屋に分ける仕組み。
 * 部屋の名前はそのままだとファイル名に使えない文字が混ざるので、指紋に直して使う。
 * 画像だけは部屋をまたいで共通（中身の指紋で参照しており、重複を避けたいため）。
 */
let spacePrefix = "";

async function computeSpacePrefix() {
  const sp = (state.space || "").trim();
  if (!sp) { spacePrefix = ""; return; }
  const d = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(sp));
  spacePrefix = [...new Uint8Array(d)].slice(0, 6).map((b) => b.toString(16).padStart(2, "0")).join("") + "/";
}
const docName = (name) => spacePrefix + name;

/* ── Yjs と手元のデータの変換 ─────────────────── */
const cardKey = (c) => JSON.stringify([c.type, c.x, c.y, c.w, c.h, c.props]);

/** いまの画面の内容を Yjs 側へ書き写す（変わったものだけ） */
function pushLocal() {
  if (!state.boardDoc || state.applyingRemote) return;
  const cur = app.getCurrent();
  if (!cur || cur.id !== state.boardId) return;

  const snap = app.serializeBoard();
  const yCards = state.boardDoc.getMap("cards");
  const yStrokes = state.boardDoc.getMap("strokes");
  const yMeta = state.boardDoc.getMap("meta");

  state.boardDoc.transact(() => {
    if (yMeta.get("name") !== snap.name) yMeta.set("name", snap.name);

    const seen = new Set();
    for (const c of snap.cards) {
      seen.add(c.id);
      const k = cardKey(c);
      if (state.lastSent.get("c:" + c.id) === k) continue;   // 変わっていない
      yCards.set(c.id, { type: c.type, x: c.x, y: c.y, w: c.w, h: c.h, props: c.props });
      state.lastSent.set("c:" + c.id, k);
    }
    for (const id of [...yCards.keys()]) {
      if (seen.has(id)) continue;
      if (!state.lastSent.has("c:" + id)) continue;   // 見たことがない＝他の端末の新しいカード
      yCards.delete(id);
      state.lastSent.delete("c:" + id);
    }

    const seenS = new Set();
    for (const s of snap.strokes) {
      seenS.add(s.id);
      if (state.lastSent.has("s:" + s.id)) continue;          // ストロークは書き換わらない
      yStrokes.set(s.id, { token: s.token, width: s.width, pts: s.pts });
      state.lastSent.set("s:" + s.id, 1);
    }
    for (const id of [...yStrokes.keys()]) {
      if (seenS.has(id)) continue;
      if (!state.lastSent.has("s:" + id)) continue;   // 同上
      yStrokes.delete(id);
      state.lastSent.delete("s:" + id);
    }
  }, "local");

  pushIndex(snap);
}

function pushIndex(snap) {
  if (!state.indexDoc) return;
  const list = state.indexDoc.getMap("boards");
  list.set(snap.id, { name: snap.name, updatedAt: snap.updatedAt });
}

/** Yjs 側の内容を画面へ反映する */
function pullRemote() {
  if (!state.boardDoc) return;
  if (app.isBusy()) { schedulePull(); return; }    // 描いている最中は割り込まない

  const cur = app.getCurrent();
  if (!cur || cur.id !== state.boardId) return;

  const yCards = state.boardDoc.getMap("cards");
  const yStrokes = state.boardDoc.getMap("strokes");
  const yMeta = state.boardDoc.getMap("meta");

  const data = {
    v: 1, id: state.boardId,
    name: yMeta.get("name") || cur.name,
    viewport: { x: app.viewport.x, y: app.viewport.y, zoom: app.viewport.zoom },
    cards: [...yCards.entries()].map(([id, c]) => ({ id, ...c })),
    strokes: [...yStrokes.entries()].map(([id, s]) => ({ id, ...s }))
  };

  // 名前だけは、中身が同じでも追いかける
  if (data.name && data.name !== cur.name) {
    cur.name = data.name;
    const el = document.getElementById("boardName");
    if (el && document.activeElement !== el) el.value = data.name;
    app.saveNow();
  }

  // 中身が同じなら描き直さない（画面のちらつきと選択解除を避ける）
  const now = app.serializeBoard();
  if (sameBoard(now, data)) return;

  state.applyingRemote = true;
  app.openBoardData(data);
  state.applyingRemote = false;

  rememberSent(data);
  app.saveNow();
}

function sameBoard(a, b) {
  if (a.cards.length !== b.cards.length) return false;
  if (a.strokes.length !== b.strokes.length) return false;
  const ac = new Map(a.cards.map(c => [c.id, cardKey(c)]));
  for (const c of b.cards) if (ac.get(c.id) !== cardKey(c)) return false;
  const as = new Set(a.strokes.map(s => s.id));
  for (const s of b.strokes) if (!as.has(s.id)) return false;
  return true;
}

function rememberSent(data) {
  state.lastSent.clear();
  for (const c of data.cards) state.lastSent.set("c:" + c.id, cardKey(c));
  for (const s of data.strokes) state.lastSent.set("s:" + s.id, 1);
}

const schedulePush = () => {
  clearTimeout(state.pushTimer);
  state.pushTimer = setTimeout(pushLocal, PUSH_DELAY);
};
const schedulePull = () => {
  clearTimeout(state.pullTimer);
  state.pullTimer = setTimeout(pullRemote, PULL_DELAY);
};

/* ── 接続 ───────────────────────────────────── */
function makeProvider(name, doc) {
  return new HocuspocusProvider({
    url: state.url,
    name,
    document: doc,
    token: state.token || undefined,
    onStatus: ({ status }) => {
      state.status = status === "connected" ? "online" : "connecting";
      paint();
    },
    onAuthenticationFailed: () => {
      state.status = "error";
      paint();
      app.toast("合言葉が違うため同期できません");
    },
    onDisconnect: () => { state.status = "connecting"; paint(); }
  });
}

function disconnect() {
  for (const p of [state.boardProvider, state.indexProvider]) {
    if (p) { try { p.destroy(); } catch (e) {} }
  }
  state.boardProvider = state.indexProvider = null;
  state.boardDoc = state.indexDoc = null;
  state.boardId = null;
  state.autoOpened = false;
  state.lastSent.clear();
}

async function connect() {
  disconnect();
  if (!state.enabled || !state.url) { state.status = "off"; paint(); return; }

  state.status = "connecting";
  paint();
  await computeSpacePrefix();

  state.indexDoc = new Y.Doc();
  state.indexProvider = makeProvider(docName("index"), state.indexDoc);
  state.indexDoc.getMap("boards").observe(() => mergeIndex());

  openBoardDoc(app.getCurrent());
}

function openBoardDoc(cur) {
  if (!cur || !state.enabled || !state.url) return;
  if (state.boardId === cur.id) return;

  if (state.boardProvider) { try { state.boardProvider.destroy(); } catch (e) {} }
  state.boardId = cur.id;
  state.lastSent.clear();
  state.boardDoc = new Y.Doc();
  state.boardProvider = makeProvider(docName("board:" + cur.id), state.boardDoc);

  const onUpdate = (_u, origin) => { if (origin !== "local") schedulePull(); };
  state.boardDoc.on("update", onUpdate);

  // 初回同期の順序が肝心。
  //   1) 手元の内容を送る（相手のものは消さない＝上の削除条件）
  //   2) そのうえで、合流した結果を受け取る
  // 逆にすると、まだ空のサーバの内容で手元が上書きされて消える。
  state.boardProvider.on("synced", () => {
    pushLocal();
    schedulePull();
  });
}

/** 遠隔のボード一覧を、手元の一覧に反映する（新規追加と、名前の追従の両方） */
async function mergeIndex() {
  if (!state.indexDoc) return;
  const remote = state.indexDoc.getMap("boards");
  const localIdx = (await app.storage.get("boards")) || [];
  const byId = new Map(localIdx.map(b => [b.id, b]));
  let added = 0, renamed = 0;

  // 遠隔の一覧から消えたものは、この端末からも取り除く
  const remoteIds = new Set(remote.keys());
  for (const id of [...state.knownRemote]) {
    if (!remoteIds.has(id)) {
      state.knownRemote.delete(id);
      await app.forgetBoard(id);
    }
  }
  for (const id of remoteIds) state.knownRemote.add(id);

  for (const [id, meta] of remote.entries()) {
    const name = meta.name || "無題のボード";
    const updatedAt = meta.updatedAt || Date.now();
    const local = byId.get(id);

    if (!local) {
      localIdx.push({ id, name, updatedAt });
      // 中身はまだ無いので、空の器だけ作る。開いた瞬間に同期が流し込む
      await app.storage.set("board:" + id, {
        v: 1, id, name, updatedAt, cards: [], strokes: [], viewport: { x: 0, y: 0, zoom: 1 }
      });
      added++;
      continue;
    }

    // 更新時刻は黙って追いかける（これで一覧を作り直すと、押しかけの行が消えてしまう）
    local.updatedAt = Math.max(updatedAt, local.updatedAt || 0);

    // 名前が変わったときだけ、一覧の描き直しが要る
    if (local.name !== name) {
      local.name = name;
      renamed++;
      const cur = app.getCurrent();
      if (cur && cur.id === id) {
        cur.name = name;
        const el = document.getElementById("boardName");
        if (el && document.activeElement !== el) el.value = name;
      }
    }
  }

  if (added || renamed) {
    await app.storage.set("boards", localIdx);
    app.renderBoardList();
    if (added) app.toast(`他の端末のボードを ${added} 件見つけました`);
  }
  maybeOpenNewest();
}

/** 起動直後、手元が空なら「いちばん最近さわったボード」を自動で開く。
 *  これは一度きり。以後は利用者がボードを選ぶ操作を絶対に邪魔しない。
 *  （空のボードへ切り替えるたびに引き戻していた不具合の対策） */
async function maybeOpenNewest() {
  if (state.autoOpened || !state.indexDoc) return;
  const cur = app.getCurrent();
  if (!cur) return;

  state.autoOpened = true;   // 判定した時点で役目は終わり。結果によらず二度と動かさない

  // 手元のボードに何か描いてあるなら、勝手に切り替えない
  if (app.cards.length > 0 || app.ink.strokes.length > 0) return;

  let best = null;
  for (const [id, meta] of state.indexDoc.getMap("boards").entries()) {
    if (id === cur.id) continue;
    const t = meta.updatedAt || 0;
    if (!best || t > best.updatedAt) best = { id, name: meta.name || "無題のボード", updatedAt: t };
  }
  if (!best) return;

  await app.switchBoard(best.id);
  app.toast(`「${best.name}」を開きました`);
}

/* ── 画像の受け渡し ───────────────────────────
 * 1枚の画像 = 1つの文書 "asset:<指紋>"。
 * ボード本体には指紋しか入らないので、線を1本足すたびに画像が飛ぶことはない。
 * 指紋は中身から作るため、同じ画像を何度貼っても実体はひとつで済む。
 */
const ASSET_KEEP_MS = 8000;      // 送ったあと、書き込みが終わるまで開いておく時間
const ASSET_WAIT_MS = 20000;     // 取り寄せをあきらめるまでの時間

function openAssetDoc(hash) {
  const doc = new Y.Doc();
  const provider = makeProvider("asset:" + hash, doc);
  return { doc, provider };
}

/** 他の端末（サーバ）から画像を取り寄せる */
async function fetchAsset(hash) {
  if (!state.enabled || !state.url) return null;
  const { doc, provider } = openAssetDoc(hash);
  try {
    const bytes = await new Promise((resolve) => {
      const done = (v) => { clearTimeout(timer); resolve(v); };
      const timer = setTimeout(() => done(null), ASSET_WAIT_MS);
      const read = () => {
        const v = doc.getMap("blob").get("data");
        if (v) done(v instanceof Uint8Array ? v : new Uint8Array(v));
      };
      provider.on("synced", () => { read(); setTimeout(() => done(null), 1200); });
      doc.getMap("blob").observe(read);
    });
    return bytes;
  } finally {
    setTimeout(() => { try { provider.destroy(); } catch (e) {} }, 200);
  }
}

/** 画像をサーバへ預ける。既にあるなら何もしない */
async function putAsset(hash, bytes, mime) {
  if (!state.enabled || !state.url) return;
  const { doc, provider } = openAssetDoc(hash);
  await new Promise((resolve) => {
    const timer = setTimeout(resolve, ASSET_WAIT_MS);
    provider.on("synced", () => {
      clearTimeout(timer);
      const blob = doc.getMap("blob");
      if (!blob.get("data")) {
        blob.set("data", bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes));
        if (mime) blob.set("mime", mime);
      }
      resolve();
    });
  });
  // 送り終わるまで少し開いたままにする
  setTimeout(() => { try { provider.destroy(); } catch (e) {} }, ASSET_KEEP_MS);
}

/* ── 本体に差し込む ─────────────────────────── */
/** 削除したボードを、同期側の一覧からも取り除く */
async function removeBoard(id) {
  if (!state.indexDoc) return;
  state.knownRemote.delete(id);
  state.indexDoc.getMap("boards").delete(id);
  if (state.boardId === id && state.boardProvider) {
    try { state.boardProvider.destroy(); } catch (e) {}
    state.boardProvider = null;
    state.boardDoc = null;
    state.boardId = null;
  }
}

app.setHooks({
  change: schedulePush,
  open: (cur) => { openBoardDoc(cur); },
  fetchAsset,
  putAsset,
  removeBoard
});

// 起動直後は本体の boot() がまだ走っているので、少し待ってから繋ぐ
setTimeout(connect, 800);

export { state as syncState, connect, disconnect };
